import actions from './actions'

/**
 * Conversation Explorer slice state. Domain rows (conversations, messages,
 * members, receipts, …) are typed loosely as `any[]` on purpose — their shapes
 * live with the components that render them (e.g. `Message`, `Conversation`), and
 * importing those here would couple redux to the view layer.
 */
export interface ConversationExplorerState {
  // Left — conversation list (ENG-36887)
  conversationList: any[]
  conversationListCursor: Record<string, any>
  // UID picked in the "On behalf of" filter. Display-only for now: a 1:1 is shown
  // from that user's side, so their own name drops out of the title.
  onBehalfOf: string
  selectedConversationId: string
  // The selected conversation object, captured on select. The center header + right
  // panel read this so they survive a list FILTER that drops the open conversation
  // from `conversationList` (else the header falls back to the raw id + 0 members).
  selectedConversation: any
  areConversationsLoading: boolean
  // Center — message thread (ENG-36888)
  messageList: any[]
  messageListCursor: Record<string, any>
  selectedMessageId: string
  areMessagesLoading: boolean
  // Right — supporting data (group members + user-flag records), lazily loaded
  groupMembers: any[]
  areMembersLoading: boolean
  membersPage: number
  membersHasMore: boolean
  flaggedMessages: any[]
  areFlaggedLoading: boolean
  // Right — per-message reactors (GET /messages/{id}/reactions)
  messageReactors: any[]
  areReactorsLoading: boolean
  // Right — per-member read/delivered receipts (GET /messages/{id})
  messageReceipts: any[]
  areReceiptsLoading: boolean
  // Center — thread replies keyed by parent message id (GET /messages/{id}/thread)
  threadReplies: Record<string, any[]>
  threadLoadingId: string
  // Deep-link: message the thread should scroll to once loaded (cleared after)
  pendingScrollMessageId: string
}

const initialState: ConversationExplorerState = {
  conversationList: [],
  conversationListCursor: {},
  onBehalfOf: '',
  selectedConversationId: '',
  selectedConversation: null,
  areConversationsLoading: false,
  messageList: [],
  messageListCursor: {},
  selectedMessageId: '',
  areMessagesLoading: false,
  groupMembers: [],
  areMembersLoading: false,
  membersPage: 1,
  membersHasMore: false,
  flaggedMessages: [],
  areFlaggedLoading: false,
  messageReactors: [],
  areReactorsLoading: false,
  messageReceipts: [],
  areReceiptsLoading: false,
  threadReplies: {},
  threadLoadingId: '',
  pendingScrollMessageId: '',
}

// Only SET_STATE (partial merge) and RESET mutate the slice; sagas do the work.
interface ConversationExplorerAction {
  type: string
  payload?: Partial<ConversationExplorerState>
}

export default function conversationExplorerReducer(
  state: ConversationExplorerState = initialState,
  action: ConversationExplorerAction = { type: '' },
): ConversationExplorerState {
  switch (action.type) {
    case actions.SET_STATE:
      return { ...state, ...action.payload }
    // Clears everything — dispatched when the active app changes so one app's
    // conversations/messages never leak into another.
    case actions.RESET:
      return initialState
    // Everything downstream of a selection, leaving the list and filters alone.
    case actions.CLEAR_SELECTION:
      return {
        ...state,
        selectedConversationId: '',
        selectedConversation: null,
        messageList: [],
        messageListCursor: {},
        selectedMessageId: '',
        groupMembers: [],
        membersPage: 1,
        membersHasMore: false,
        flaggedMessages: [],
        messageReactors: [],
        messageReceipts: [],
        threadReplies: {},
        threadLoadingId: '',
        pendingScrollMessageId: '',
      }
    default:
      return state
  }
}
